import { Box, Chip, Typography } from '@mui/material';
import type { TestSession, SimulationRun } from '@k-ucap/shared';

interface WorkbenchHeaderProps {
  projectName: string;
  testName?: string | null;
  session: TestSession | null;
  run: SimulationRun | null;
  elapsedMs: number;
  passCount: number;
  warnCount: number;
  failCount: number;
}

const formatElapsed = (ms: number): string => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remaining}s` : `${remaining}s`;
};

export const WorkbenchHeader = ({
  projectName,
  testName,
  session,
  run,
  elapsedMs,
  passCount,
  warnCount,
  failCount,
}: WorkbenchHeaderProps) => {
  const status = run?.status ?? session?.status ?? 'idle';
  const statusColor =
    status === 'completed' ? 'success' : status === 'running' ? 'info' : status === 'error' ? 'error' : 'default';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        mb: 1,
      }}
      data-testid="workbench-header"
    >
      <Box>
        <Typography variant="caption" color="text.secondary">
          Project
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {projectName}
        </Typography>
      </Box>
      {testName && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Test
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {testName}
          </Typography>
        </Box>
      )}
      {session && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Session
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {session.id.slice(0, 18)}…
          </Typography>
        </Box>
      )}
      <Chip label={status} size="small" color={statusColor as 'success' | 'info' | 'error' | 'default'} />
      <Box>
        <Typography variant="caption" color="text.secondary">
          Elapsed
        </Typography>
        <Typography variant="body2">{formatElapsed(elapsedMs)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Chip label={`PASS ${passCount}`} size="small" color="success" variant="outlined" />
        <Chip label={`WARN ${warnCount}`} size="small" color="warning" variant="outlined" />
        <Chip label={`FAIL ${failCount}`} size="small" color="error" variant="outlined" />
      </Box>
    </Box>
  );
};
